import {
	addDays,
	addMonths,
	differenceInCalendarDays,
	endOfMonth,
	format,
	isWeekend,
	startOfMonth,
	subMonths,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { parseScheduleDate } from "./gantt.ts";

export interface TimelineDomain {
	start: Date;
	end: Date;
	totalDays: number;
}

export interface TimelineMonth {
	key: string;
	label: string;
	offsetDays: number;
	days: number;
}

export interface TimelineDay {
	key: string;
	label: string;
	weekend: boolean;
	today: boolean;
}

export interface TimelineBar {
	offsetDays: number;
	days: number;
}

export function buildTimelineDomain(
	dates: (string | undefined)[],
	today: Date,
	extraMonthsBefore = 0,
	extraMonthsAfter = 0,
): TimelineDomain {
	const times = dates
		.map(parseScheduleDate)
		.filter((date): date is Date => date !== undefined)
		.map((date) => date.getTime());
	times.push(today.getTime());

	const start = startOfMonth(
		subMonths(new Date(Math.min(...times)), 1 + extraMonthsBefore),
	);
	const end = endOfMonth(
		addMonths(new Date(Math.max(...times)), 1 + extraMonthsAfter),
	);
	return {
		start,
		end,
		totalDays: differenceInCalendarDays(end, start) + 1,
	};
}

export function timelineMonths(domain: TimelineDomain): TimelineMonth[] {
	const months: TimelineMonth[] = [];
	let cursor = domain.start;
	while (cursor <= domain.end) {
		const monthEnd = endOfMonth(cursor);
		months.push({
			key: format(cursor, "yyyy-MM"),
			label: format(cursor, "MMMM yyyy", { locale: ptBR }),
			offsetDays: differenceInCalendarDays(cursor, domain.start),
			days: differenceInCalendarDays(monthEnd, cursor) + 1,
		});
		cursor = addDays(monthEnd, 1);
	}
	return months;
}

export function timelineDays(
	domain: TimelineDomain,
	today: Date,
): TimelineDay[] {
	const todayKey = format(today, "yyyy-MM-dd");
	return Array.from({ length: domain.totalDays }, (_, index) => {
		const date = addDays(domain.start, index);
		const key = format(date, "yyyy-MM-dd");
		return {
			key,
			label: format(date, "d"),
			weekend: isWeekend(date),
			today: key === todayKey,
		};
	});
}

export function timelineBar(
	domain: TimelineDomain,
	start: string | undefined,
	end: string | undefined,
): TimelineBar | null {
	const startDate = parseScheduleDate(start);
	const endDate = parseScheduleDate(end);
	if (!startDate || !endDate) return null;

	const from = Math.max(0, differenceInCalendarDays(startDate, domain.start));
	const to = Math.min(
		domain.totalDays,
		differenceInCalendarDays(endDate, domain.start) + 1,
	);
	if (to <= from) return null;
	return { offsetDays: from, days: to - from };
}

export function dayIndexToDate(domain: TimelineDomain, index: number): string {
	const clamped = Math.min(Math.max(index, 0), domain.totalDays - 1);
	return format(addDays(domain.start, clamped), "yyyy-MM-dd");
}

export function dateToDayIndex(
	domain: TimelineDomain,
	value: string,
): number | null {
	const date = parseScheduleDate(value);
	if (!date) return null;
	return differenceInCalendarDays(date, domain.start);
}

export function todayIndex(domain: TimelineDomain, today: Date): number | null {
	const index = differenceInCalendarDays(today, domain.start);
	return index >= 0 && index < domain.totalDays ? index : null;
}
